/**
 * File: apps/qbench-cp/reactapp/src/Order/SelectTests/EditTests/styles.js
 *
 * Purpose: Provides the shared styled-components used to lay out the sample-assignment table
 *     and its accordion-style rows.
 *
 * Scope: EditTests test-assignment table within order test selection.
 *
 * Tags: customer-portal, orders, tests
 *
 * Overview: Defines the table title, the grid used for the overall table, and the accordion
 *     header/collapse pair used per sample row, including disabled-state opacity/cursor/pointer-
 *     events styling and rounded-corner treatment that differs between collapsed and expanded
 *     states.
 *
 * Exports: TableTitle, AccordionHeader, AccordionCollapse, TableGrid — styled-components used
 *     across the assignment table.
 */

import styled from 'styled-components';
import { Wrapper as CheckboxWrapper } from './AssignmentTable/Checkbox';

const spacer = '.75rem';
const sectionBg = (props) => props.theme.color.palette.grey[50];
const opacity = (props) => (props.disabled ? '.6' : '1');
const pointerEvents = (props) => (props.disabled ? 'none' : 'auto');
const cursor = (props) => (props.disabled ? 'not-allowed' : 'default');

export const TableTitle = styled.h5`
  font-size: 0.875rem;
  font-weight: 600;
`;

export const AccordionHeader = styled.div`
  display: grid;
  grid: auto / 40px 1fr 8fr;
  gap: ${spacer};
  padding: ${spacer} 0;
  cursor: ${(props) => (props.isEmpty ? 'default' : 'pointer')};
  user-select: none;
  opacity: ${opacity};
  transition: background-color 0.1s ease-in-out;

  &:hover {
    background: ${sectionBg};
  }
  > :first-child {
    place-self: center;
  }
  > :nth-child(2) {
    place-self: center start;
  }
  ${(props) =>
    props.isCollapsed
      ? `
      border-radius: ${spacer};
    `
      : `
      border-radius: ${spacer} ${spacer} 0 0;
      background: ${props.theme.color.palette.grey[50]};
    `}
`;

export const AccordionCollapse = styled.div`
  background: ${sectionBg};
  grid-column: 2 / span 3;
  margin-top: -${spacer};
  cursor: ${cursor};
  opacity: ${opacity};
  pointer-events: ${pointerEvents};
  padding: 1rem 3.5rem 2rem 3.5rem;
  border-radius: 0 0 ${spacer} ${spacer};
  display: ${(props) => (props.isCollapsed ? 'none' : 'block')};
`;

export const TableGrid = styled.div`
  display: grid;
  grid: auto / 30px 40px 1fr 8fr;
  align-items: center;
  grid-row-gap: ${spacer};
  gap: ${spacer};
  color: ${(props) => props.theme.color.palette.text.main};
  font-size: 0.875rem;
  font-weight: 400;
  line-heigth: 1.4;

  > ${CheckboxWrapper} {
    place-self: center center;
  }

  > ${AccordionHeader} {
    grid-column: 2 / span 3;
  }
`;
